import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PageContainer, PageHeader } from '@/components/layout/PageContainer';

import { useAssetLibraryPermissions } from '../useAssetLibraryPermissions';
import { BRIEF_CHANNELS, type BriefStatus } from './api';
import { BriefDrawer } from './BriefDrawer';
import { BriefList } from './BriefList';
import { parseBriefsSearch, type BriefsSearch } from './search';

/**
 * My Briefs: the briefs the caller made, as drafts to finish and requests
 * already sent.
 *
 * Tab and channel live in the URL, owned by the router (see `search.ts`), so a
 * reload or a shared link lands on the same view, and any navigation to this
 * route — the sidebar link included — moves the view with it.
 */

const TABS = [
  { key: 'draft', label: 'Draft' },
  { key: 'requested', label: 'Requested' },
] as const satisfies readonly { key: BriefStatus; label: string }[];

const TAB_PANEL_ID = 'my-briefs-tabpanel';

export function MyBriefsPage() {
  const {
    permissions,
    isReady: isPermissionsReady,
    isError: isPermissionsError,
    refetch: refetchPermissions,
  } = useAssetLibraryPermissions();

  // Read on every render, not kept in state: the URL is the only copy.
  // Parsed again here because the route's own validation lives in router.tsx,
  // which imports this page.
  const search = parseBriefsSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const activeTab: BriefStatus = search.tab ?? 'draft';
  const channel = search.channel ?? '';

  const navigate = useNavigate();
  // Replace, not push: switching tabs is not somewhere Back should return to.
  const updateSearch = useCallback(
    (next: BriefsSearch) =>
      void navigate({
        to: '/assets/briefs',
        search: next,
        replace: true,
      }),
    [navigate],
  );

  const handleTabChange = (tab: BriefStatus) =>
    updateSearch({ tab: tab === 'draft' ? undefined : tab, channel: search.channel });

  const handleChannelChange = (value: string) =>
    updateSearch({ tab: search.tab, channel: value || undefined });

  // Opening pushes, so Back closes the drawer; closing replaces, so Back then
  // doesn't reopen it. Same as the asset overlay.
  const handleOpenBrief = (briefId: string) =>
    void navigate({ to: '/assets/briefs', search: { ...search, brief: briefId } });
  const handleCloseBrief = () => updateSearch({ ...search, brief: undefined });

  const canBrief = permissions.can_brief;

  return (
    <>
      <PageHeader>
        <Breadcrumb
          // Always a node, even when empty: without actions Breadcrumb adds its
          // own sticky header chrome, which inside PageHeader doubles it — for
          // every briefer while permissions load, and for good otherwise.
          actions={
            <div className="ml-auto flex items-center">
              {canBrief && (
                <label className="flex items-center gap-2 text-[12.5px] text-slate-600">
                  Channel
                  <select
                    value={channel}
                    onChange={(event) => handleChannelChange(event.target.value)}
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-[12.5px] text-slate-700 focus:border-[#0014cc] focus:outline-none"
                  >
                    <option value="">All</option>
                    {BRIEF_CHANNELS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          }
        />
        {canBrief && (
          <div className="px-4">
            <div className="flex items-end gap-0 border-b border-slate-200" role="tablist">
              {TABS.map(({ key, label }) => {
                const isActive = activeTab === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={TAB_PANEL_ID}
                    onClick={() => handleTabChange(key)}
                    className={`-mb-px whitespace-nowrap border-b-2 px-4 pb-2.5 pt-1 text-sm font-medium transition-all ${
                      isActive
                        ? 'border-[#0014cc] text-[#0014cc]'
                        : 'border-transparent text-slate-400 hover:border-slate-300 hover:text-slate-600'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </PageHeader>

      <PageContainer>
        {isPermissionsError ? (
          <Message title="Couldn’t load your permissions">
            <p>
              My Briefs needs them to know whether you can brief. This is a loading problem, not a
              change to your access.
            </p>
            <button
              type="button"
              onClick={refetchPermissions}
              className="mt-3 rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3]"
            >
              Try again
            </button>
          </Message>
        ) : !isPermissionsReady ? null : !canBrief ? (
          // Reached by URL only: the nav entry is hidden for this role.
          <Message title="My Briefs is for roles that brief">
            <p>Your role can’t create briefs, so there is nothing to list here.</p>
          </Message>
        ) : (
          <div id={TAB_PANEL_ID} role="tabpanel">
            <BriefList
              key={activeTab}
              status={activeTab}
              channel={channel}
              // Drafts are only ever one's own, so the column belongs to
              // Requested, and only where others' briefs can show.
              showCreatedBy={
                (permissions.can_view_all_briefs || permissions.can_view_market_briefs) &&
                activeTab === 'requested'
              }
              onOpen={handleOpenBrief}
              onClearChannel={() => handleChannelChange('')}
            />
          </div>
        )}
      </PageContainer>

      {canBrief && search.brief && (
        <BriefDrawer key={search.brief} briefId={search.brief} onClose={handleCloseBrief} />
      )}
    </>
  );
}

function Message({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white">
      <div className="max-w-md text-center text-[12.5px] leading-relaxed text-slate-500">
        <h2 className="mb-2 text-[15.5px] font-bold text-slate-800">{title}</h2>
        {children}
      </div>
    </div>
  );
}
